import { writable } from 'svelte/store';
import {
  DEFAULT_THEME_ID,
  getTheme,
  isKnownColorMode,
  isKnownThemeId,
} from '../themes/index.js';

/**
 * Appearance state — two independent axes.
 *
 *   theme id     which design system is active (`data-theme` on <html>)
 *   colour mode  dark or light within that design system (`.dark` on <html>)
 *
 * Both live here and are written through to localStorage, which is what the
 * pre-paint bootstrap in `index.html` reads; `preferences.js` syncs them with
 * the account once there is a session.
 *
 * MODE_KEY keeps its historical name so an existing install does not lose the
 * dark/light choice it already made when the theme axis was added.
 */
const MODE_KEY = 'sdi-theme';
const THEME_KEY = 'sdi-theme-name';
/** "Dettagli inWD": 'on' or 'off'. Read by the pre-paint bootstrap too. */
const DETAILS_KEY = 'sdi-inwd-details';
/**
 * Set by the pre-paint bootstrap once it has moved this browser's stored
 * appearance onto the inwd default (see `index.html`). Written here as well,
 * before every save: what this store saves is a value of the current
 * registry, so storage holding it must never be migrated again. Without it, a
 * browser whose bootstrap could not write the marker on the first load would
 * have the user's next choice rewritten to inwd light on a later load.
 */
const MIGRATED_KEY = 'sdi-appearance-migrated-v2';

const DEFAULT_MODE = 'light';

function readStored(key, isValid, fallback) {
  if (typeof window === 'undefined') return fallback;
  try {
    const stored = localStorage.getItem(key);
    return isValid(stored) ? stored : fallback;
  } catch {
    // Private mode / storage disabled: fall back rather than break the app.
    return fallback;
  }
}

/**
 * Save one appearance value, marker first.
 *
 * The order is what protects the save. A value written without the marker is
 * exposed to the bootstrap's one-shot migration on the next load, which would
 * rewrite it; so if the marker cannot be written the value is not written
 * either, and the choice simply does not survive the reload. If the marker
 * sticks and the value does not, the previous value stays — never migrated,
 * never half-moved.
 */
function persist(key, value) {
  try {
    localStorage.setItem(MIGRATED_KEY, '1');
  } catch {
    return false;
  }
  try {
    localStorage.setItem(key, value);
    return true;
  } catch {
    // Nothing to do — the preference simply does not survive the reload.
    return false;
  }
}

/**
 * Live appearance state.
 *
 * These, not localStorage, are the source of truth while the tab is open.
 * Reading the choice back out of storage would make both axes depend on a
 * write that is allowed to fail silently: with site data blocked, `persist()`
 * is a no-op, so a toggle derived from the stored value would compute the same
 * "next" state forever, and setting one axis would re-stamp the other from a
 * stale default. Storage is where the preference is *saved*, not where it
 * lives.
 */
let currentThemeId = readStored(THEME_KEY, isKnownThemeId, DEFAULT_THEME_ID);
let currentMode = readStored(MODE_KEY, isKnownColorMode, DEFAULT_MODE);
let currentDetails = readStored(DETAILS_KEY, (v) => v === 'on' || v === 'off', 'off') === 'on';

/**
 * Keep the browser chrome (Android address bar, iOS status bar, PWA splash)
 * on the active theme's page colour. Without this the chrome stays on the
 * inwd light white baked into index.html even in dark mode.
 */
function applyBrowserChrome(themeId, mode) {
  const meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) return;
  const { preview } = getTheme(themeId);
  meta.setAttribute('content', preview[mode].background);
}

function applyToDocument() {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.dataset.theme = currentThemeId;
  root.classList.toggle('dark', currentMode === 'dark');
  // Stamped whatever the theme: only the inwd stylesheet reads it, so under
  // another theme it is inert and the choice survives a switch back.
  if (currentDetails) root.dataset.inwdDetails = 'on';
  else delete root.dataset.inwdDetails;
  applyBrowserChrome(currentThemeId, currentMode);
}

function createColorMode() {
  const { subscribe, set } = writable(currentMode);

  /** Apply `next`, persist it, and keep the store in sync. */
  function apply(next) {
    currentMode = isKnownColorMode(next) ? next : DEFAULT_MODE;
    persist(MODE_KEY, currentMode);
    applyToDocument();
    set(currentMode);
    return currentMode;
  }

  return {
    subscribe,
    set: apply,
    toggle: () => apply(currentMode === 'dark' ? 'light' : 'dark'),
  };
}

function createThemeId() {
  const { subscribe, set } = writable(currentThemeId);

  function apply(next) {
    currentThemeId = isKnownThemeId(next) ? next : DEFAULT_THEME_ID;
    persist(THEME_KEY, currentThemeId);
    applyToDocument();
    set(currentThemeId);
    return currentThemeId;
  }

  return {
    subscribe,
    set: apply,
    select: apply,
  };
}

function createInwdDetails() {
  const { subscribe, set } = writable(currentDetails);

  function apply(next) {
    currentDetails = next === true;
    persist(DETAILS_KEY, currentDetails ? 'on' : 'off');
    applyToDocument();
    set(currentDetails);
    return currentDetails;
  }

  return {
    subscribe,
    set: apply,
    toggle: () => apply(!currentDetails),
  };
}

/** Dark / light within the active theme. */
export const colorMode = createColorMode();

/**
 * "Dettagli inWD": the illustrated details of the inwd theme (botanical
 * navigation frame, status chips, forest page loader). Off by default; a
 * theme without the capability ignores it (see `details` in the registry).
 */
export const inwdDetails = createInwdDetails();

/** Which design system is active — see `src/themes/index.js`. */
export const themeId = createThemeId();

/**
 * Stamp the appearance the stores hold onto <html>. Writes nothing.
 *
 * `index.html` already runs the same stamping inline before first paint so the
 * page never flashes the wrong theme; this call is what makes the DOM agree
 * with the stores after hydration (an id the registry no longer ships has
 * already resolved to the default here), and what recovers if the inline
 * bootstrap was skipped.
 *
 * It deliberately saves nothing: re-saving what was read would write the
 * migration marker over storage the bootstrap could not migrate, freezing a
 * value the next load would otherwise have moved. Storage only changes when
 * a value is chosen — by the user, or by the account after sign-in.
 */
export function initAppearance() {
  applyToDocument();
}
